import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { doc, onSnapshot, collection, query, where, orderBy, getDoc } from "firebase/firestore";
import { db } from "../../../Firebase";
import { V3Room, V3Event, V3Problem, V3Submission, V3User } from "../types/v3types";
import styled, { keyframes, createGlobalStyle } from "styled-components";
import { getCustomComponent } from "../components/customComponents";

const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    overflow: hidden;
    background: #0a0a0a;
  }
`;

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #000;
  position: relative;
  overflow: hidden;
  background-image: linear-gradient(45deg, #000 25%, #1a1a1a 25%, #1a1a1a 50%, #000 50%, #000 75%, #1a1a1a 75%, #1a1a1a);
  background-size: 40px 40px;
`;

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.8; }
  50% { transform: scale(1.05); opacity: 0.6; }
  100% { transform: scale(1); opacity: 0.8; }
`;

const BackgroundAnimation = styled.div`
  position: absolute;
  width: 200%;
  height: 200%;
  top: -50%;
  left: -50%;
  background: radial-gradient(circle at 20% 50%, rgba(220, 20, 60, 0.3), transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(139, 0, 0, 0.3), transparent 50%),
              radial-gradient(circle at 40% 20%, rgba(255, 69, 0, 0.3), transparent 50%);
  animation: ${pulse} 20s ease-in-out infinite;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  width: 90%;
  max-width: 1600px;
  height: 90%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 30px;
`;

const WaitingScreen = styled.div`
  text-align: center;
  color: white;
`;

const Title = styled.h1`
  font-size: 72px;
  margin-bottom: 20px;
  color: #fff;
  text-shadow: 0 4px 20px rgba(220, 20, 60, 0.5);
  animation: ${pulse} 3s ease-in-out infinite;
  background: linear-gradient(45deg, #dc143c, #ff4444);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const SubTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 40px;
  opacity: 0.9;
`;

const AccessCodeDisplay = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 2px solid #dc143c;
  border-radius: 20px;
  padding: 40px 60px;
  margin-top: 40px;
  box-shadow: 0 0 30px rgba(220, 20, 60, 0.3);
`;

const AccessCodeLabel = styled.div`
  font-size: 24px;
  margin-bottom: 20px;
  opacity: 0.8;
`;

const AccessCode = styled.div`
  font-size: 64px;
  font-weight: bold;
  letter-spacing: 12px;
  color: #ff4444;
  text-shadow: 0 0 20px rgba(255, 68, 68, 0.5);
`;

const countdown = keyframes`
  0% { transform: scale(0.5); opacity: 0; }
  50% { transform: scale(1.2); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const CountdownDisplay = styled.div`
  font-size: 200px;
  font-weight: bold;
  color: #ff4444;
  text-shadow: 0 0 40px rgba(255, 68, 68, 0.8), 0 0 80px rgba(220, 20, 60, 0.5);
  animation: ${countdown} 1s ease-out;
`;

const ProblemContainer = styled.div`
  flex: 7;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.95);
  border: 4px solid #dc143c;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.4);
`;

const ProblemHeader = styled.div`
  background: linear-gradient(135deg, #dc143c 0%, #8b0000 100%);
  color: white;
  padding: 30px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 4px solid #ff0000;
`;

const ProblemNumber = styled.h2`
  font-size: 32px;
  margin: 0;
`;

const Timer = styled.div`
  font-size: 48px;
  font-weight: bold;
  padding: 10px 30px;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 10px;
  border: 2px solid #ff4444;
  text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
`;

const ProblemContent = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: auto;
`;

const ProblemImage = styled.img`
  max-width: 90%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(220, 20, 60, 0.3);
  border: 3px solid #dc143c;
`;

const AnswerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  background: rgba(255, 255, 255, 0.95);
  border: 4px solid #dc143c;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.4);
`;

const AnswerSection = styled.div`
  flex: 1;
  padding: 40px;
  display: flex;
  flex-direction: column;
`;

const AnswerTitle = styled.h2`
  font-size: 36px;
  margin-bottom: 30px;
  color: #dc143c;
  font-weight: bold;
`;

const AnswerText = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: #dc143c;
  margin-bottom: 30px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
`;

const RankingContainer = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border: 4px solid #dc143c;
  border-radius: 20px;
  padding: 40px;
  overflow: auto;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.4);
`;

const RankingTitle = styled.h1`
  font-size: 48px;
  text-align: center;
  margin-bottom: 40px;
  color: #dc143c;
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
`;

const RankingList = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const slideIn = keyframes`
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const RankingItem = styled.div<{ rank: number }>`
  display: flex;
  align-items: center;
  padding: 20px 30px;
  margin-bottom: 15px;
  background: ${props => {
    if (props.rank === 1) return "linear-gradient(135deg, #ffd700, #ffed4b)";
    if (props.rank === 2) return "linear-gradient(135deg, #c0c0c0, #e8e8e8)";
    if (props.rank === 3) return "linear-gradient(135deg, #cd7f32, #e6a857)";
    return "#f8f9fa";
  }};
  border-radius: 15px;
  border: ${props => props.rank <= 3 ? "3px solid #dc143c" : "1px solid #e0e0e0"};
  box-shadow: 0 4px 15px ${props => props.rank <= 3 ? "rgba(220, 20, 60, 0.3)" : "rgba(0, 0, 0, 0.1)"};
  animation: ${slideIn} 0.5s ease-out;
  animation-delay: ${props => props.rank * 0.1}s;
  animation-fill-mode: both;
`;

const RankNumber = styled.div`
  font-size: 36px;
  font-weight: bold;
  margin-right: 30px;
  min-width: 60px;
  text-align: center;
`;

const RankName = styled.div`
  flex: 1;
  font-size: 28px;
  font-weight: 500;
`;

const RankScore = styled.div`
  font-size: 32px;
  font-weight: bold;
`;

const LiveRankingContainer = styled.div`
  flex: 3;
  height: 100%;
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  border: 4px solid #dc143c;
  border-radius: 20px;
  padding: 20px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(220, 20, 60, 0.4);
  display: flex;
  flex-direction: column;
`;

const LiveRankingTitle = styled.h3`
  color: #ff4444;
  font-size: 24px;
  margin: 0 0 15px 0;
  text-align: center;
  text-shadow: 0 0 15px rgba(255, 68, 68, 0.5);
  font-weight: bold;
  flex-shrink: 0;
`;

const LiveRankingList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: white;
  font-size: 16px;
  flex: 1;
  overflow-y: auto;
  padding-right: 10px;
`;

const LiveRankingItem = styled.div<{ rank: number }>`
  padding: 6px 15px;
  background: rgba(0, 255, 0, 0.1);
  border-left: 4px solid ${props => {
    if (props.rank === 1) return "#ffd700";
    if (props.rank === 2) return "#c0c0c0";
    if (props.rank === 3) return "#cd7f32";
    return "#00ff00";
  }};
  color: ${props => {
    if (props.rank === 1) return "#ffd700";
    if (props.rank === 2) return "#e0e0e0";
    if (props.rank === 3) return "#cd7f32";
    return "#00ff00";
  }};
  font-size: ${props => props.rank <= 3 ? "18px" : "16px"};
  font-weight: ${props => props.rank <= 3 ? "bold" : "normal"};
  animation: ${fadeIn} 0.3s ease-out;
  animation-delay: ${props => props.rank * 0.05}s;
  animation-fill-mode: both;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.2s;
  
  &:hover {
    background: rgba(0, 255, 0, 0.2);
  }
`;


const correctSound = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;


const V3Screen: React.FC = () => {
  const { projectId, roomId } = useParams<{ projectId: string; roomId: string }>();
  const [room, setRoom] = useState<V3Room | null>(null);
  const [event, setEvent] = useState<V3Event | null>(null);
  const [currentProblem, setCurrentProblem] = useState<V3Problem | null>(null);
  const [participants, setParticipants] = useState<V3User[]>([]);
  const [submissions, setSubmissions] = useState<V3Submission[]>([]);
  const [countdown, setCountdown] = useState<number>(0);
  const [timer, setTimer] = useState<number>(0);
  const [autoSwitchInterval, setAutoSwitchInterval] = useState<NodeJS.Timeout | null>(null);
  const [problemSubmissions, setProblemSubmissions] = useState<V3Submission[]>([]);

  useEffect(() => {
    if (!roomId) return;

    // Subscribe to room updates
    const unsubscribeRoom = onSnapshot(doc(db, "v3rooms", roomId), async (docSnapshot) => {
      if (docSnapshot.exists()) {
        const roomData = docSnapshot.data() as V3Room;
        setRoom(roomData);

        // Load event data
        if (roomData.eventId) {
          const eventDocRef = doc(db, "v3events", roomData.eventId);
          const eventDoc = await getDoc(eventDocRef);
          if (eventDoc.exists()) {
            const eventData = eventDoc.data() as V3Event;
            setEvent(eventData);
            
            if (eventData.problems && eventData.problems[roomData.currentProblemIndex]) {
              setCurrentProblem(eventData.problems[roomData.currentProblemIndex]);
            }
          }
        }
      }
    });

    // Subscribe to participants
    const participantsQuery = query(
      collection(db, "v3users"),
      where("projectId", "==", projectId)
    );
    
    const unsubscribeParticipants = onSnapshot(participantsQuery, (snapshot) => {
      const users = snapshot.docs
        .map(doc => doc.data() as V3User)
        .filter(user => room?.participants?.includes(user.userId));
      setParticipants(users);
    });

    // Subscribe to submissions
    const submissionsQuery = query(
      collection(db, "v3submissions"),
      where("roomId", "==", roomId)
    );
    
    const unsubscribeSubmissions = onSnapshot(submissionsQuery, (snapshot) => {
      const subs = snapshot.docs.map(doc => doc.data() as V3Submission);
      setSubmissions(subs);
    });

    return () => {
      unsubscribeRoom();
      unsubscribeParticipants();
      unsubscribeSubmissions();
    };
  }, [roomId, projectId, room?.participants]);

  useEffect(() => {
    if (room?.displayMode === "countdown") {
      setCountdown(3);
      const interval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [room?.displayMode]);

  useEffect(() => {
    if (room && currentProblem) {
      // Initialize timer based on problem status and server time
      if (room.problemStatuses?.[currentProblem.problemId] === "timing") {
        if (room.problemStartTimes?.[currentProblem.problemId]) {
          // Calculate remaining time based on server start time
          const startTime = room.problemStartTimes[currentProblem.problemId].toMillis();
          const currentTime = Date.now();
          const elapsed = Math.round((currentTime - startTime) / 1000);
          const remaining = Math.max(0, currentProblem.timeLimit - elapsed);
          setTimer(remaining);
        } else if (room.problemTimers?.[currentProblem.problemId]) {
          setTimer(room.problemTimers[currentProblem.problemId]);
        } else {
          setTimer(currentProblem.timeLimit);
        }
      } else {
        setTimer(currentProblem.timeLimit);
      }
      setProblemSubmissions([]); // Reset submissions for new problem
    }
  }, [room, currentProblem]);

  // Timer countdown based on problem status
  useEffect(() => {
    // Only countdown when status is "timing"
    if (room && currentProblem && room.problemStatuses?.[currentProblem.problemId] === "timing" && timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => {
          // Double check status is still "timing" before decrementing
          if (room.problemStatuses?.[currentProblem.problemId] !== "timing" || prev <= 0) {
            clearInterval(interval);
            return prev;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [room, currentProblem, room?.problemStatuses, timer]);

  // Update problem submissions in real-time
  useEffect(() => {
    if (currentProblem && submissions.length > 0) {
      const currentProblemSubs = submissions
        .filter(sub => sub.problemId === currentProblem.problemId)
        .sort((a, b) => {
          // Sort by correct first
          if (a.isCorrect && !b.isCorrect) return -1;
          if (!a.isCorrect && b.isCorrect) return 1;
          if (a.isCorrect && b.isCorrect) {
            // For correct answers, sort by time taken (faster first)
            return a.timeTaken - b.timeTaken;
          }
          return 0;
        });
      setProblemSubmissions(currentProblemSubs);
    }
  }, [submissions, currentProblem]);

  // Auto switch images effect
  useEffect(() => {
    if (autoSwitchInterval) {
      clearInterval(autoSwitchInterval);
      setAutoSwitchInterval(null);
    }

    if (room?.displayMode === "problem" && currentProblem && currentProblem.imageSettings?.switchMode === "auto") {
      const allImages = [...(currentProblem.beforeImageUrls || []), ...currentProblem.imageUrls];
      if (allImages.length > 1) {
        const switchInterval = (currentProblem.imageSettings.autoSwitchInterval || 5) * 1000;
        const interval = setInterval(() => {
          // This would trigger a Firestore update in a real implementation
          // For now, we just handle the display locally
        }, switchInterval);
        setAutoSwitchInterval(interval);
      }
    }

    return () => {
      if (autoSwitchInterval) {
        clearInterval(autoSwitchInterval);
      }
    };
  }, [room?.displayMode, currentProblem]);

  const calculateScore = (userId: string) => {
    return submissions
      .filter(sub => sub.userId === userId && sub.isCorrect)
      .reduce((total, sub) => total + sub.score + (sub.kachidokiPoints || 0), 0);
  };

  const getRanking = () => {
    return participants
      .map(user => ({
        user,
        score: calculateScore(user.userId)
      }))
      .sort((a, b) => b.score - a.score);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatSubmissionTime = (submission: V3Submission) => {
    const seconds = submission.timeTaken;
    return `${Math.floor(seconds / 60)}:${(seconds % 60).toFixed(1).padStart(4, '0')}`;
  };

  const getUsername = (userId: string) => {
    const user = participants.find(p => p.userId === userId);
    return user?.username || 'Unknown';
  };

  if (!room || !event) {
    return (
      <Container>
        <GlobalStyle />
        <BackgroundAnimation />
        <Content>
          <WaitingScreen>
            <Title>Loading...</Title>
          </WaitingScreen>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <GlobalStyle />
      <BackgroundAnimation />
      <Content>
        {room.displayMode === "waiting" && (
          <WaitingScreen>
            <Title>{event.eventName}</Title>
            <SubTitle>{room.roomName}</SubTitle>
            {!room.isPublic && room.accessCode && (
              <AccessCodeDisplay>
                <AccessCodeLabel>アクセスコード</AccessCodeLabel>
                <AccessCode>{room.accessCode}</AccessCode>
              </AccessCodeDisplay>
            )}
            <SubTitle style={{ marginTop: 60 }}>
              参加者: {participants.length}人
            </SubTitle>
          </WaitingScreen>
        )}

        {room.displayMode === "countdown" && countdown > 0 && (
          <CountdownDisplay>{countdown}</CountdownDisplay>
        )}

        {(room.displayMode === "problem" || room.displayMode === "problemWithTimer") && currentProblem && (
          <>
            <ProblemContainer>
              <ProblemHeader>
                <ProblemNumber>
                  問題 {room.currentProblemIndex + 1} / {event.problems.length}
                </ProblemNumber>
                {room.problemStatuses?.[currentProblem.problemId] === "timing" && (
                  <Timer>{formatTime(timer)}</Timer>
                )}
              </ProblemHeader>
              <ProblemContent>
                {/* Check problem status - show appropriate message based on status */}
                {room.problemStatuses?.[currentProblem.problemId] === "waiting" ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '80px 20px', 
                    color: '#fff',
                    background: 'rgba(100, 100, 100, 0.9)',
                    borderRadius: '20px',
                    width: '80%',
                    maxWidth: '600px'
                  }}>
                    <h2 style={{ fontSize: '48px', marginBottom: '30px' }}>準備中...</h2>
                    <p style={{ fontSize: '24px' }}>まもなく問題が表示されます</p>
                  </div>
                ) : room.problemStatuses?.[currentProblem.problemId] === "finished" ? (
                  <div style={{ 
                    textAlign: 'center', 
                    padding: '80px 20px', 
                    color: '#fff',
                    background: 'rgba(80, 80, 80, 0.9)',
                    borderRadius: '20px',
                    width: '80%',
                    maxWidth: '600px'
                  }}>
                    <h2 style={{ fontSize: '48px', marginBottom: '30px' }}>時間切れ</h2>
                    <p style={{ fontSize: '24px' }}>この問題は終了しました</p>
                  </div>
                ) : (
                  <>
                {(() => {
                  // Check for custom component first
                  if (currentProblem.customComponent) {
                    const CustomComponent = getCustomComponent(currentProblem.customComponent.type);
                    if (CustomComponent) {
                      return (
                        <CustomComponent
                          problemId={currentProblem.problemId}
                          currentImageIndex={room.currentImageIndex || 0}
                          imageUrls={currentProblem.imageUrls}
                          beforeImageUrls={currentProblem.beforeImageUrls}
                          customProps={currentProblem.customComponent.props}
                        />
                      );
                    }
                  }
                  
                  // Default image display
                  const allImages = [...(currentProblem.beforeImageUrls || []), ...currentProblem.imageUrls];
                  const currentIndex = room.currentImageIndex || 0;
                  const imageUrl = allImages[currentIndex] || allImages[0];
                  
                  return imageUrl ? (
                    <ProblemImage src={imageUrl} alt="Problem" />
                  ) : null;
                })()}
                </>
                )}
              </ProblemContent>
            </ProblemContainer>
            
            {problemSubmissions.filter(sub => sub.isCorrect).length > 0 && (
              <LiveRankingContainer>
                <LiveRankingTitle>🏆 正解者 ({problemSubmissions.filter(sub => sub.isCorrect).length}名)</LiveRankingTitle>
                <LiveRankingList>
                  {problemSubmissions
                    .filter(sub => sub.isCorrect)
                    .map((sub, index) => (
                      <LiveRankingItem key={sub.submissionId} rank={index + 1}>
                        <div style={{ flex: 1, fontSize: '22px', fontWeight: '500' }}>
                          {getUsername(sub.userId)}
                        </div>
                        <div style={{ fontSize: '18px', color: '#ffd700' }}>
                          <span style={{ marginRight: '15px' }}>{sub.timeTaken.toFixed(3)}秒</span>
                          <span style={{ fontWeight: 'bold' }}>+{sub.score.toFixed(3)}点</span>
                        </div>
                      </LiveRankingItem>
                    ))}
                </LiveRankingList>
              </LiveRankingContainer>
            )}
          </>
        )}

        {room.displayMode === "answer" && currentProblem && (
          <AnswerContainer>
            <AnswerSection>
              <AnswerTitle>正解</AnswerTitle>
              <AnswerText>{currentProblem.answers.join(" または ")}</AnswerText>
              {currentProblem.explanation && (
                <div>
                  <AnswerTitle>解説</AnswerTitle>
                  <div style={{ fontSize: '24px', lineHeight: '1.6' }}>
                    {currentProblem.explanation}
                  </div>
                </div>
              )}
              
              {/* Show top 5 correct answerers */}
              {problemSubmissions.filter(sub => sub.isCorrect).length > 0 && (
                <div style={{ marginTop: '30px' }}>
                  <AnswerTitle>正解者一覧</AnswerTitle>
                  <div style={{ 
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '15px',
                    justifyContent: 'center'
                  }}>
                    {problemSubmissions
                      .filter(sub => sub.isCorrect)
                      .map((sub, index) => (
                        <div key={sub.submissionId} style={{
                          padding: '10px 20px',
                          background: index === 0 ? 'linear-gradient(135deg, rgba(255, 215, 0, 0.2), rgba(255, 215, 0, 0.1))' :
                                     index === 1 ? 'linear-gradient(135deg, rgba(192, 192, 192, 0.2), rgba(192, 192, 192, 0.1))' :
                                     index === 2 ? 'linear-gradient(135deg, rgba(205, 127, 50, 0.2), rgba(205, 127, 50, 0.1))' :
                                     'rgba(220, 20, 60, 0.1)',
                          borderRadius: '10px',
                          border: index === 0 ? '2px solid #ffd700' :
                                  index === 1 ? '2px solid #c0c0c0' :
                                  index === 2 ? '2px solid #cd7f32' :
                                  '2px solid #dc143c',
                          fontSize: index < 3 ? '24px' : '20px',
                          fontWeight: 'bold',
                          color: index === 0 ? '#ffd700' :
                                 index === 1 ? '#c0c0c0' :
                                 index === 2 ? '#cd7f32' :
                                 '#dc143c'
                        }}>
                          {getUsername(sub.userId)}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </AnswerSection>
            {currentProblem.answerImageUrls && currentProblem.answerImageUrls[0] && (
              <AnswerSection>
                <ProblemImage src={currentProblem.answerImageUrls[0]} alt="Answer" />
              </AnswerSection>
            )}
          </AnswerContainer>
        )}

        {(room.displayMode === "ranking" || room.displayMode === "finalRanking") && (
          <RankingContainer>
            <RankingTitle>
              {room.displayMode === "finalRanking" ? "最終結果" : "ランキング"}
            </RankingTitle>
            <RankingList>
              {getRanking().map((item, index) => (
                <RankingItem key={item.user.userId} rank={index + 1}>
                  <RankNumber>{index + 1}</RankNumber>
                  <RankName>{item.user.username}</RankName>
                  <RankScore>{item.score.toFixed(3)}点</RankScore>
                </RankingItem>
              ))}
            </RankingList>
          </RankingContainer>
        )}
      </Content>
    </Container>
  );
};

export default V3Screen;